import { CSSProperties, ComponentType, Fragment, useState } from "react"

import {
  DYNAMIC_TAXONOMY_BO_OPTIONS,
  MATCH_RGX,
  interpolate,
} from "routes/Base/Admin/Taxes/TaxExempt/utils"
import { EntityType, TaxonomyPattern } from "models/taxonomy"
import { css, cx } from "@emotion/css"

import Button from "ui/buttons/Button"
import { EmptyState } from "components/EmptyState"
import { ExternalIdPatternModal } from "components/Modals/ExternalIdPatternModal"
import { Loading } from "components/Loading"
import { StyledTable } from "components/Rable/StyledTable"
import { Switch } from "@material-ui/core"
import { useAuthenticated } from "providers/A12n"
import { useI18n } from "providers/I18n"

type Col = {
  name: string
  cell: ComponentType<CellProps>
  align?: "center" | "left" | "right"
  width?: CSSProperties["width"]
}

type CellProps = {
  row: TaxonomyPattern
  name: string
  update: (checked: boolean, id: string) => void
}

const generateColumns = () => {
  const c: Col[] = [
    { name: "entityType", cell: ReadOnlyCell, align: "left" },
    { name: "content", cell: PatternCell, align: "left" },
    { name: "status", cell: StatusCell, align: "center", width: "112px" },
  ]
  return c
}

type Props = {
  taxonomies: {
    id: string
    clientAgencyConfigId: string
    entityType: EntityType
    enabled: boolean
    content?: string
  }[]
  updateRow: (checked: boolean, id: string) => void
  loading: boolean
}

export function TaxonomiesAcrossMT(props: Props) {
  const { t } = useI18n()
  const { hasAuthority } = useAuthenticated()
  const { taxonomies, updateRow, loading } = props
  const [modalOpenIndex, onModalOpenIndex] = useState<number>(-1)
  const cols = generateColumns()

  const onOpen = (index: number) => {
    onModalOpenIndex(index)
  }
  const onClose = () => {
    onModalOpenIndex(-1)
  }

  return (
    <>
      <div className={styles.title}>
        <strong>{t("taxonomy.across-media-types.title")}</strong>
      </div>
      {loading ? (
        <Loading />
      ) : taxonomies?.length ? (
        <StyledTable className={styles.table}>
          <thead>
            <tr role="row">
              {cols.map((col) => (
                <th
                  key={col.name}
                  role="columnheader"
                  className={cx(col.align)}
                  style={{ width: col.width }}
                >
                  <span>{t(`agency.client.taxonomy.${col.name}`)}</span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {taxonomies?.map((row, rIndex) => (
              <tr key={rIndex} role="row">
                {cols.map((col, cIndex) => {
                  const { name, cell: Cell, align = "left" } = col
                  const showBtn =
                    name === "content" &&
                    hasAuthority(["AUTH_MANAGE_CLIENT_TAXONOMY"])
                  return (
                    <td className={align} key={`td${cIndex}`}>
                      <div className={styles.wrapper}>
                        <Cell row={row} name={name} update={updateRow} />
                        {showBtn && (
                          <Button
                            onClick={() => onOpen(rIndex)}
                            color="primary"
                            className={styles.textButton}
                          >
                            {t(
                              "agency.client.taxonomy.external-id-pattern.edit",
                            )}
                          </Button>
                        )}
                      </div>
                    </td>
                  )
                })}
              </tr>
            ))}
          </tbody>
        </StyledTable>
      ) : (
        <EmptyState title={t("agency.client.taxonomy.external-id.no-result")} />
      )}
      <ExternalIdPatternModal
        open={modalOpenIndex >= 0}
        updateStatus={updateRow}
        onClose={onClose}
        lineItemTaxonomy={false}
        taxonomy={taxonomies[modalOpenIndex]}
      />
    </>
  )
}

function PatternCell(props: CellProps) {
  const { row } = props

  const { parts, remain } = interpolate(
    row.content || "",
    MATCH_RGX,
    DYNAMIC_TAXONOMY_BO_OPTIONS,
  )

  return (
    <div>
      {parts.map((p, idx) => {
        const { simple, interpolated, replace } = p
        return (
          <Fragment key={`parts_${idx}`}>
            {simple.replace(replace, "")}
            <span>{interpolated}</span>
          </Fragment>
        )
      })}
      {remain}
    </div>
  )
}

function ReadOnlyCell(props: CellProps) {
  const { row, name } = props
  const { t } = useI18n()

  return (
    <div>
      {t(`agency.client.taxonomy.${row[name as keyof TaxonomyPattern]}`)}
    </div>
  )
}

function StatusCell(props: CellProps) {
  const { row, update } = props
  const { hasAuthority } = useAuthenticated()

  return (
    <Switch
      name="taxonomiesAcrossMediaTypes"
      value={row.enabled}
      color="primary"
      defaultChecked={!!row.enabled}
      disabled={!row.content || !hasAuthority(["AUTH_MANAGE_CLIENT_TAXONOMY"])}
      checked={!!row.enabled}
      onChange={(_, checked) => update(checked, row?.id)}
    />
  )
}

const styles = {
  wrapper: css`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
  `,
  textButton: css`
    --btn-padding: 0;
    --btn-min-width: 0;
    --btn-line-height: 14px;
    --btn-color: inherit;
    --btn-font-weight: normal;
    --btn-bg-active: transparent;
    --btn-bg-focus-ring-color: transparent;
    --btn-bg-hover: transparent;
    padding-left: var(--spacing-m);
    padding-right: var(--spacing-m);

    .label {
      justify-content: flex-start;
    }

    &:focus {
      box-shadow: none;
    }

    &:hover {
      background-color: transparent;
      border: 1px solid transparent;
    }
  `,
  table: css`
    .RebaseTable__header-cell {
      padding: 0 12px;
      font-size: 12px;
      color: var(--black);
      font-weight: bold;
      text-transform: capitalize;
    }
    .RebaseTable__grid-cell {
    }

    .RebaseTable__body-row:hover {
      background-color: var(--primary-light);
    }

    .RebaseTable__body-row--hovered {
      background-color: var(--primary-light);
    }

    .RebaseTable__cell {
      &[data-column-id^="__"] {
        border-right: none;
        padding: 0;
      }
      :last-child {
        display: flex;
        justify-content: center;
      }
    }
    .RebaseTable__cell:focus-within {
      box-shadow: none;
    }
  `,
  title: css`
    font-size: 14px;
    color: var(--black);
    padding-bottom: var(--spacing-m);
  `,
}
